/**
 * SocialPreneur — "Asta Cafe" bespoke landing tokens (Layer-3 tenant override).
 * Faithful recreation of the handoff
 * (`client design handoffs/design_handoff_socialpreneur/Asta Cafe Landing.dc.html`):
 * a premium, content-first marketing page for SocialPreneur Inc.'s Asta Cafe
 * wellness-coffee blend. Gold-on-espresso, Oswald + Mulish + Pinyon Script.
 *
 * The GOLD/ESPRESSO PALETTE lives as CSS variables in `asta.css` (scoped to
 * `.asta-root`) — exactly like the prototype's `:root` vars — so inline styles use
 * `var(--gold)` etc. and the `:hover` rules share the same source. That keeps this
 * public page's look OWNED here (the documented AGP/upmi/mhyel Layer-3 exception to
 * "no hardcoded colors"); the runtime tenant theme (SocialPreneur green) is left
 * untouched so the member portal + shared transactional surfaces stay green.
 *
 * This file holds only the NON-color tokens: fonts, media/asset paths, and the
 * static content the chrome renders (nav, gallery slides, footer columns).
 */

// ── Type ────────────────────────────────────────────────────────────────────
// Loaded via TENANT_PRESETS.socialpreneur.theme.fontLinks — gen-tenant-theme.ts
// bakes the <link> into index.html at BUILD time (Oswald + Pinyon Script alongside
// the portal's existing Mulish + Inter). Run gen-tenant-theme before `dev` or these
// fall back to system fonts.
export const FONT_DISPLAY = "'Oswald', system-ui, sans-serif"; // headings, eyebrows, nav, buttons
export const FONT_BODY = "'Mulish', system-ui, sans-serif"; // body copy
export const FONT_SCRIPT = "'Pinyon Script', cursive"; // "Asta" / "A Better Coffee." accents

// ── Brand ────────────────────────────────────────────────────────────────────
/** The Asta Cafe gold shield (client-provided) — a transparent-background PNG, so
 * it sits directly on the dark chrome (no plate) with a soft drop-shadow. */
export const LOGO = '/tenants/socialpreneur/asta-cafe-logo.png';

// Contact email, gallery slides, and the 3 photo slots moved to the
// site-content manifest (`SOCIALPRENEUR_SITE` in @mlm/shared) so the admin
// "Site Pages" tab can edit them. The committed assets stay under
// `apps/member/public/tenants/socialpreneur/` and remain the defaults there.

// ── Header anchor nav (in-page smooth-scroll targets) ────────────────────────
export const NAV: { label: string; href: string }[] = [
  { label: 'The Blend', href: '#blend' },
  { label: 'Ingredients', href: '#ingredients' },
  { label: 'Astaxanthin', href: '#astaxanthin' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'How to Use', href: '#how' },
  { label: 'Certifications', href: '#trust' },
];

// ── Footer columns (chrome). Anchor hrefs smooth-scroll; route hrefs (/...) nav.
//
// This is an INFORMATION SITE — SocialPreneur sells nothing here and takes no
// self-service signups. So every link below is an in-page anchor to a section
// that actually exists: no `#` placeholders (they silently jump to the top,
// which reads as a broken site), no route into the shop, and no "Become a
// Distributor" into `/signup`. Enquiries go to the Contact band, which carries
// the mailto. `Wholesale` was a handoff placeholder with nothing behind it and
// is gone. Keep it that way: don't add a link here until its target exists.
export const FOOTER_COLS: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: 'Learn',
    links: [
      { label: 'The Blend', href: '#blend' },
      { label: 'Ingredients', href: '#ingredients' },
      { label: 'Astaxanthin', href: '#astaxanthin' },
      { label: 'How to Use', href: '#how' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About SocialPreneur', href: '/about-us' },
      { label: 'Our Mission', href: '/about-us#mission' },
      { label: 'Certifications', href: '#trust' },
      { label: 'Contact', href: '#contact' },
    ],
  },
  {
    title: 'Get Asta Cafe',
    links: [
      { label: 'Where to Buy', href: '#contact' },
      { label: 'FAQ', href: '#faq' },
    ],
  },
];
